import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getNotifications, markNotificationRead } from '../api/message';
import { Notification } from '../types/user';

const TopAppBar: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const fetchNotifications = useCallback(() => {
    if (!currentUser) return;
    getNotifications().then(setNotifications).catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleNotifClick = async (n: Notification) => {
    if (!n.isRead) {
      await markNotificationRead(n.id).catch(() => {});
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, isRead: true } : x));
    }
    setNotifOpen(false);
    if ((n.type === 'NewMessage' || n.type === 'QuoteSent' || n.type === 'QuoteAccepted' || n.type === 'QuoteDeclined') && n.jobId) {
      navigate(`/messages?jobId=${n.jobId}`);
    } else if ((n.type === 'DirectJobSent' || n.type === 'DirectJobRejected' || n.type === 'QuoteDeclined') && n.directJobId) {
      navigate(`/direct-jobs/${n.directJobId}`);
    } else if (n.type === 'DirectJobUnderReview') {
      navigate('/admin/direct-jobs');
    } else if (n.type === 'JobUnderReview') {
      navigate('/admin/jobs');
    } else if (n.jobId) {
      navigate(`/jobs/${n.jobId}`);
    }
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/');
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="bg-surface dark:bg-on-background shadow-sm flex justify-between items-center w-full px-container-margin h-16 sticky top-0 z-50">
      <div className="flex items-center gap-2">
        <img src="/buildonLogo.png" alt="Buildon" className="h-10 w-auto" />
      </div>

      <nav className="hidden md:flex items-center gap-8">
        {[
          { to: '/', label: 'Home', end: true },
          { to: '/contractors', label: 'Contractors' },
          { to: '/categories', label: 'Categories' },
          { to: '/jobs', label: 'Jobs' },
        ].map(({ to, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              isActive
                ? 'font-label-md text-label-md text-primary dark:text-primary-fixed font-semibold px-3 py-2 rounded-lg bg-primary/10 dark:bg-primary-fixed/10'
                : 'font-label-md text-label-md text-outline dark:text-outline-variant hover:bg-surface-container-low dark:hover:bg-surface-container-highest transition-colors px-3 py-2 rounded-lg'
            }
          >
            {label}
          </NavLink>
        ))}
        {currentUser && (
          <NavLink
            to="/my-jobs"
            className={({ isActive }) =>
              isActive
                ? 'font-label-md text-label-md text-primary dark:text-primary-fixed font-semibold px-3 py-2 rounded-lg bg-primary/10 dark:bg-primary-fixed/10'
                : 'font-label-md text-label-md text-outline dark:text-outline-variant hover:bg-surface-container-low dark:hover:bg-surface-container-highest transition-colors px-3 py-2 rounded-lg'
            }
          >
            My Jobs
          </NavLink>
        )}
        {currentUser?.isContractor && (
          <NavLink
            to="/contractor-profile"
            className={({ isActive }) =>
              isActive
                ? 'font-label-md text-label-md text-primary dark:text-primary-fixed font-semibold px-3 py-2 rounded-lg bg-primary/10 dark:bg-primary-fixed/10'
                : 'font-label-md text-label-md text-outline dark:text-outline-variant hover:bg-surface-container-low dark:hover:bg-surface-container-highest transition-colors px-3 py-2 rounded-lg'
            }
          >
            My Profile
          </NavLink>
        )}
      </nav>

      <div className="flex items-center gap-3">
        {/* Notification bell */}
        {currentUser && (
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => { setNotifOpen(prev => !prev); if (!notifOpen) fetchNotifications(); }}
              className="relative p-2 rounded-full hover:bg-surface-container-low transition-colors"
              aria-label="Notifications"
            >
              <span className="material-symbols-outlined text-outline" data-icon="notifications">notifications</span>
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-error text-on-error text-[10px] font-bold flex items-center justify-center leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-surface rounded-xl shadow-lg border border-outline-variant overflow-hidden z-50">
                <div className="px-4 py-3 border-b border-outline-variant flex items-center justify-between">
                  <p className="text-sm font-semibold text-on-surface">Notifications</p>
                  {unreadCount > 0 && (
                    <span className="text-xs text-primary">{unreadCount} unread</span>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-on-surface-variant text-center">No notifications yet.</p>
                ) : (
                  <div className="divide-y divide-outline-variant max-h-80 overflow-y-auto">
                    {notifications.map(n => (
                      <button
                        key={n.id}
                        onClick={() => handleNotifClick(n)}
                        className={`w-full text-left px-4 py-3 hover:bg-surface-container-low transition-colors flex items-start gap-3 ${!n.isRead ? 'bg-primary/5' : ''}`}
                      >
                        <span
                          className={`material-symbols-outlined text-[20px] flex-shrink-0 mt-0.5 ${!n.isRead ? 'text-primary' : 'text-outline'}`}
                          style={!n.isRead ? { fontVariationSettings: "'FILL' 1" } : undefined}
                          data-icon="notifications"
                        >
                          notifications
                        </span>
                        <div className="min-w-0">
                          <p className={`text-sm ${!n.isRead ? 'font-semibold text-on-surface' : 'text-on-surface-variant'}`}>
                            {n.message}
                          </p>
                          <p className="text-xs text-outline mt-0.5">
                            {new Date(n.createdAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                          </p>
                        </div>
                        {!n.isRead && (
                          <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!currentUser && (
          <button className="p-2 rounded-full hover:bg-surface-container-low transition-colors" aria-label="Notifications">
            <span className="material-symbols-outlined text-outline" data-icon="notifications">notifications</span>
          </button>
        )}

        {currentUser ? (
          <>
            {!currentUser.isContractor && (
              <Link
                to="/jobs/new"
                className="hidden md:inline-flex items-center gap-1 border border-primary text-primary px-4 py-2 rounded-lg font-label-bold text-label-bold hover:bg-primary hover:text-on-primary active:scale-95 transition-all"
              >
                <span className="material-symbols-outlined text-[18px]" data-icon="add">add</span>
                Post a Job
              </Link>
            )}

            {/* Profile dropdown */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(prev => !prev)}
                className="flex items-center gap-1.5 pl-2 pr-3 py-1.5 rounded-full hover:bg-surface-container-low transition-colors"
                aria-haspopup="true"
                aria-expanded={menuOpen}
              >
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                    person
                  </span>
                </span>
                <span className="material-symbols-outlined text-outline text-[18px]">
                  {menuOpen ? 'expand_less' : 'expand_more'}
                </span>
              </button>

              {menuOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-surface rounded-xl shadow-lg border border-outline-variant overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-outline-variant">
                    <p className="text-xs text-outline truncate">{currentUser.email}</p>
                  </div>

                  <div className="py-1">
                    <Link
                      to="/my-jobs"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-on-surface hover:bg-surface-container-low transition-colors"
                    >
                      <span className="material-symbols-outlined text-[20px] text-outline">work_history</span>
                      My Jobs
                    </Link>

                    <Link
                      to="/profile"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-on-surface hover:bg-surface-container-low transition-colors"
                    >
                      <span className="material-symbols-outlined text-[20px] text-outline">manage_accounts</span>
                      My Account
                    </Link>

                    {currentUser.isContractor && (
                      <Link
                        to="/contractor-profile"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-on-surface hover:bg-surface-container-low transition-colors"
                      >
                        <span className="material-symbols-outlined text-[20px] text-outline">business_center</span>
                        Contractor Profile
                      </Link>
                    )}

                    {currentUser.isContractor && (
                      <Link
                        to="/resource-catalogue"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-on-surface hover:bg-surface-container-low transition-colors"
                      >
                        <span className="material-symbols-outlined text-[20px] text-outline">receipt_long</span>
                        Resource Catalogue
                      </Link>
                    )}

                    {currentUser.isContractor && (
                      <Link
                        to="/direct-jobs"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-on-surface hover:bg-surface-container-low transition-colors"
                      >
                        <span className="material-symbols-outlined text-[20px] text-outline">forward_to_inbox</span>
                        Direct Jobs
                      </Link>
                    )}

                    {currentUser.isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-on-surface hover:bg-surface-container-low transition-colors"
                      >
                        <span className="material-symbols-outlined text-[20px] text-outline">admin_panel_settings</span>
                        Admin Dashboard
                      </Link>
                    )}
                  </div>

                  <div className="border-t border-outline-variant py-1">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error hover:bg-error/5 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[20px]">logout</span>
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <Link
            to="/login"
            className="bg-secondary text-on-secondary px-6 py-2 rounded-lg font-label-bold text-label-bold active:scale-95 transition-transform"
          >
            Get Started
          </Link>
        )}
      </div>
    </header>
  );
};

export default TopAppBar;
